import { type Static, Type as t } from "@sinclair/typebox";
import { DownloadErrorCodeSchema } from "../errors";
import { PolicyVerdict, TagPolicyRule } from "./gallery";
import {
  CategorySchema,
  closed,
  DownloadQualitySchema,
  DownloadRouteSchema,
  DownloadRunnerSchema,
  DownloadStageSchema,
  Gid,
  IsoDateTime,
  literals,
  nullable,
  Ok,
  SyncFinishReason,
  SyncStreamSchema,
  Tag,
  Uuid,
} from "./primitives";
import { route, withIds } from "./route";

/**
 * /internal/* is called by the Lambdas only, never by browsers. Every request carries
 * `x-volgo-timestamp` and `x-volgo-signature` (see hmac.ts). Lambdas never touch the database.
 */

// ---- payloads that reach a Lambda without HTTP ---------------------------------------------

/** EventBridge schedule input of eh-sync. */
export const SyncInvokePayload = t.Object({ stream: SyncStreamSchema }, { additionalProperties: false });
export type SyncInvokePayloadType = Static<typeof SyncInvokePayload>;

/** The API invokes archive-dl / hitomi-dl asynchronously with this payload; details come from /internal/downloads/start. */
export const DownloadInvokePayload = t.Object({ downloadId: Uuid }, { additionalProperties: false });
export type DownloadInvokePayloadType = Static<typeof DownloadInvokePayload>;

// ---- gallery metadata as the Lambda reports it ---------------------------------------------

/** Normalised result of one `gdata` entry (produced by the shared parser in packages/core/eh). */
export const GalleryMeta = t.Object(
  {
    gid: Gid,
    token: t.String({ pattern: "^[0-9a-f]{10}$" }),
    title: t.String(),
    titleJpn: nullable(t.String()),
    category: CategorySchema,
    thumb: t.String(),
    uploader: nullable(t.String()),
    posted: IsoDateTime,
    filecount: t.Integer({ minimum: 0 }),
    filesize: t.Integer({ minimum: 0 }),
    expunged: t.Boolean(),
    rating: t.Number({ minimum: 0, maximum: 5 }),
    torrentcount: t.Integer({ minimum: 0 }),
    tags: t.Array(Tag),
    parentGid: nullable(Gid),
    firstGid: nullable(Gid),
    currentGid: nullable(Gid),
  },
  { additionalProperties: false },
);
export type GalleryMetaType = Static<typeof GalleryMeta>;

export const META_FAILURE_REASONS = ["api_error", "bad_token", "missing"] as const;
export type MetaFailureReason = (typeof META_FAILURE_REASONS)[number];

export const MetaResultItem = t.Union([
  t.Object({ gid: Gid, outcome: t.Literal("ok"), meta: GalleryMeta }, { additionalProperties: false }),
  t.Object(
    {
      gid: Gid,
      outcome: t.Literal("failed"),
      reason: literals(META_FAILURE_REASONS),
      message: t.Optional(t.String({ maxLength: 500 })),
    },
    { additionalProperties: false },
  ),
]);

// ---- sync -----------------------------------------------------------------------------------

export const SyncLease = t.Object({
  leaseId: Uuid,
  runId: t.Integer(),
  stream: SyncStreamSchema,
  cursorGid: nullable(
    t.Integer({ description: "resume `?next=<cursorGid>`; null = start from the newest page" }),
  ),
  highWaterGid: nullable(t.Integer({ description: "highest gid this stream has fully processed" })),
  maxRequests: t.Integer({
    minimum: 1,
    description: "hard cap for this run (list pages, gdata calls and removal-check pages)",
  }),
  expiresAt: IsoDateTime,
});
export type SyncLeaseType = Static<typeof SyncLease>;

export const LeaseId = t.Object({ leaseId: Uuid });

const ListedGallery = t.Object({
  gid: Gid,
  token: t.String({ pattern: "^[0-9a-f]{10}$" }),
  state: literals(["listed", "expunged"] as const),
});

export const internalRoutes = withIds({
  "internal.gdataAcquire": route({
    module: "internal",
    method: "POST",
    path: "/internal/eh/gdata/acquire",
    access: "internal",
    summary: "세 Lambda가 공유하는 gdata 요청 허가",
    body: closed({}),
    responses: {
      200: t.Union([
        closed({ granted: t.Literal(true), permitId: Uuid, validForMs: t.Integer({ minimum: 1 }) }),
        closed({ granted: t.Literal(false), retryAfterMs: t.Integer({ minimum: 1 }) }),
      ]),
    },
    errors: ["INVALID_SIGNATURE"],
    notes:
      "DB에서 전체 producer와 재호출을 합산해 4건씩 허가한다. 4건 모두 완료/만료한 뒤 2초 대기. 거부 시 retryAfterMs 뒤 재요청, 실패 시 외부 요청 금지. 허가 유효기간은 acquire 시작부터 잰 경과시간을 빼서 사용하며 gdata 호출/본문 수신을 그 안에 중단한다.",
  }),
  "internal.gdataRelease": route({
    module: "internal",
    method: "POST",
    path: "/internal/eh/gdata/release",
    access: "internal",
    summary: "gdata 요청 완료 후 공유 허가 반납",
    body: closed({ permitId: Uuid }),
    responses: { 200: Ok },
    errors: ["INVALID_SIGNATURE"],
    notes:
      "성공·실패 모두 finally에서 반납한다. 중복·이미 만료한 허가 반납은 멱등 성공. 반납 유실은 허가 만료와 5초 대기 뒤 복구한다.",
  }),
  "internal.syncLease": route({
    module: "internal",
    method: "POST",
    path: "/internal/sync/lease",
    access: "internal",
    summary: "흐름의 임대 요청(흐름마다 보유자 하나)",
    body: t.Object({ stream: SyncStreamSchema }),
    responses: {
      200: t.Object({
        lease: nullable(SyncLease),
        reason: nullable(literals(["busy", "paused", "not_due"] as const)),
      }),
    },
    errors: ["INVALID_SIGNATURE"],
    notes: "lease=null과 이유가 오면 실행을 정상 종료하고 재시도하지 않는다.",
  }),
  "internal.syncPage": route({
    module: "internal",
    method: "POST",
    path: "/internal/sync/page",
    access: "internal",
    summary: "목록 한 쪽 보고. API가 커서를 전진시키고 계속할지 정한다",
    body: t.Object({
      leaseId: Uuid,
      requestedCursor: nullable(
        t.Integer({
          description:
            "the cursor this page was fetched with: the lease's cursorGid for the first page of a run, then the nextCursor of the previous answer; null = newest page",
        }),
      ),
      items: t.Array(ListedGallery, { maxItems: 200 }),
      nextCursor: nullable(
        t.Integer({ description: "the `next=` value of the following page; null = last page" }),
      ),
      totalResults: nullable(t.Integer({ description: '"Found about N results"' })),
      requests: t.Integer({ minimum: 1, description: "requests made so far in this run" }),
    }),
    responses: {
      200: t.Object({
        accepted: t.Integer({ minimum: 0 }),
        newCount: t.Integer({ minimum: 0 }),
        knownCount: t.Integer({ minimum: 0 }),
        instruction: literals(["continue", "stop"] as const),
        stopReason: nullable(literals(["caught_up", "end_of_list", "request_limit"] as const)),
        nextCursor: nullable(t.Integer()),
        expiresAt: IsoDateTime,
      }),
    },
    errors: ["LEASE_INVALID", "CONFLICT", "INVALID_SIGNATURE"],
    notes:
      "커서는 이 호출이 성공한 뒤에만 한 쪽씩 전진한다. gid가 전부 이미 아는 것인 쪽은 `caught_up` 쪽으로 세고, 그런 쪽이 2장 연속이면 실행을 멈춘다. 쪽은 한 번씩 순서대로만 받는다: `requestedCursor`는 실행의 커서(임대의 `cursorGid`, 그다음은 직전 응답의 `nextCursor`)여야 한다. 직전에 받아들인 보고를 다시 보내면(`requestedCursor`, `items`, `nextCursor`가 같음) 같은 응답을 돌려주고 아무것도 바꾸지 않는다. 그 밖의 보고와 `stop` 뒤의 새 쪽은 CONFLICT이고 아무것도 바꾸지 않는다.",
  }),
  "internal.syncAbort": route({
    module: "internal",
    method: "POST",
    path: "/internal/sync/abort",
    access: "internal",
    summary: "사이트가 정상 응답을 하지 않아 중단. 흐름을 일시 정지한다. 자동 재시도 없음.",
    body: t.Object({
      leaseId: Uuid,
      verdict: literals(["blank", "login_required", "banned", "unknown"] as const),
      httpStatus: nullable(t.Integer()),
      requests: t.Integer({ minimum: 0 }),
      note: t.Optional(t.String({ maxLength: 500 })),
    }),
    responses: { 200: t.Object({ paused: t.Boolean(), pausedAt: nullable(IsoDateTime) }) },
    errors: ["LEASE_INVALID", "INVALID_SIGNATURE"],
  }),
  "internal.syncFinish": route({
    module: "internal",
    method: "POST",
    path: "/internal/sync/finish",
    access: "internal",
    summary: "실행의 정상 종료. 임대를 놓는다",
    body: t.Object({
      leaseId: Uuid,
      outcome: literals(["ok", "partial", "error"] as const),
      finishReason: t.Optional(SyncFinishReason),
      requests: t.Integer({ minimum: 0 }),
      note: t.Optional(t.String({ maxLength: 500 })),
    }),
    responses: { 200: Ok },
    errors: ["LEASE_INVALID", "INVALID_SIGNATURE"],
    notes: "`ok`만 lastSuccessAt을 옮긴다.",
  }),
  "internal.metaRemovalClaim": route({
    module: "internal",
    method: "POST",
    path: "/internal/meta/removal/claim",
    access: "internal",
    summary: "반복 not-found의 원본 삭제 확인을 meta 실행당 최대 1건 임대",
    body: LeaseId,
    responses: {
      200: t.Object({
        item: nullable(t.Object({ gid: Gid, token: t.String({ pattern: "^[0-9a-f]{10}$" }) })),
      }),
    },
    errors: ["LEASE_INVALID", "INVALID_SIGNATURE"],
  }),
  "internal.metaRemovalResult": route({
    module: "internal",
    method: "POST",
    path: "/internal/meta/removal/result",
    access: "internal",
    summary: "EH·인증 ExH의 삭제 확인 결과를 현재 임대와 토큰으로 보고",
    body: t.Object({
      leaseId: Uuid,
      gid: Gid,
      token: t.String({ pattern: "^[0-9a-f]{10}$" }),
      outcome: literals(["removed", "available", "inconclusive"] as const),
    }),
    responses: { 200: Ok },
    errors: ["LEASE_INVALID", "CONFLICT", "INVALID_SIGNATURE"],
    notes:
      "removed는 양쪽 HTTP404·명시적 삭제 안내를 확인한 경우에만 보고한다. available은 7일, inconclusive는 24시간 유예하며 갤러리와 완료 다운로드를 보존한다.",
  }),
  "internal.metaBatch": route({
    module: "internal",
    method: "POST",
    path: "/internal/meta/batch",
    access: "internal",
    summary: "최초 미수집과 기한이 된 최근 60일 재조회·목록 상태 재확인을 최대 25개 내준다",
    body: t.Object({ leaseId: Uuid, limit: t.Integer({ minimum: 1, maximum: 25 }) }),
    responses: {
      200: t.Object({
        items: t.Array(t.Object({ gid: Gid, token: t.String({ pattern: "^[0-9a-f]{10}$" }) }), {
          maxItems: 25,
        }),
      }),
    },
    errors: ["LEASE_INVALID", "INVALID_SIGNATURE"],
    notes:
      "최초 채우기와 재조회를 공평하게 배분한다. 게시 나이별 최소 간격은 1일 미만 3시간, 1~7일 6시간, 7~20일 24시간, 20~60일 72시간이다. 내준 행은 meta_next_at 임대/실패 유예를 지킨다.",
  }),
  "internal.metaResult": route({
    module: "internal",
    method: "POST",
    path: "/internal/meta/result",
    access: "internal",
    summary: "gdata 호출 결과를 항목별로 보고",
    body: t.Object({ leaseId: Uuid, results: t.Array(MetaResultItem, { minItems: 1, maxItems: 25 }) }),
    responses: { 200: t.Object({ stored: t.Integer({ minimum: 0 }), failed: t.Integer({ minimum: 0 }) }) },
    errors: ["LEASE_INVALID", "INVALID_SIGNATURE"],
    notes: "실패 항목은 저장 집계에 넣지 않는다. API는 저장한 행마다 galleries.policy를 다시 계산한다.",
  }),
  "internal.downloadStart": route({
    module: "internal",
    method: "POST",
    path: "/internal/downloads/start",
    access: "internal",
    summary: "다운로드 Lambda의 첫 호출: 작업을 running으로 바꾸고 받을 대상을 돌려준다",
    body: t.Object({
      downloadId: Uuid,
      runner: t.Optional(DownloadRunnerSchema),
    }),
    responses: {
      200: t.Object({
        download: t.Object({
          id: Uuid,
          gid: Gid,
          token: t.String({ pattern: "^[0-9a-f]{10}$" }),
          route: DownloadRouteSchema,
          quality: DownloadQualitySchema,
          s3Prefix: t.String({ description: "e.g. v2/g/123/archive-resample/" }),
        }),
        policy: PolicyVerdict,
      }),
    },
    errors: ["NOT_FOUND", "GALLERY_REMOVED", "POLICY_BLOCKED", "CONFLICT", "INVALID_SIGNATURE"],
    notes:
      "차단 갤러리는 POLICY_BLOCKED, 원본 삭제 갤러리는 GALLERY_REMOVED로 답하고 작업을 failed로 바꾼다. Lambda는 사이트에 아무 요청도 하지 않고 멈춰야 한다.",
  }),
  "internal.downloadProgress": route({
    module: "internal",
    method: "POST",
    path: "/internal/downloads/progress",
    access: "internal",
    summary: "진행 보고. 20분 정체 감지가 보는 생존 신호이기도 하다",
    body: t.Object({
      downloadId: Uuid,
      stage: DownloadStageSchema,
      done: t.Integer({ minimum: 0 }),
      total: nullable(t.Integer({ minimum: 0 })),
      estimatedGp: t.Optional(nullable(t.Integer({ minimum: 0 }))),
      resolvedQuality: t.Optional(DownloadQualitySchema),
      bytes: t.Optional(
        t.Object({
          done: t.Integer({ minimum: 0 }),
          total: nullable(t.Integer({ minimum: 0 })),
          rate: t.Integer({ minimum: 0, description: "recent receive rate, bytes per second" }),
        }),
      ),
    }),
    responses: {
      200: t.Object({
        ok: t.Literal(true),
        abort: t.Boolean({
          description: "true = stop now (job was cancelled, failed or the gallery became blocked)",
        }),
      }),
    },
    errors: ["NOT_FOUND", "CONFLICT", "INVALID_SIGNATURE"],
  }),
  "internal.downloadComplete": route({
    module: "internal",
    method: "POST",
    path: "/internal/downloads/complete",
    access: "internal",
    summary: "모든 페이지가 S3에 있고 매니페스트를 썼음",
    body: t.Object({
      downloadId: Uuid,
      pageCount: t.Integer({ minimum: 1 }),
      uploadedCount: t.Integer({ minimum: 0 }),
      totalBytes: t.Integer({ minimum: 0 }),
      resolvedQuality: DownloadQualitySchema,
      spentGp: t.Integer({ minimum: 0 }),
    }),
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "VALIDATION_FAILED", "INVALID_SIGNATURE"],
    notes:
      "pageCount와 uploadedCount가 같아야 한다. 다르면 VALIDATION_FAILED이고 Lambda는 UPLOAD_MISMATCH를 보고한다.",
  }),
  "internal.downloadFail": route({
    module: "internal",
    method: "POST",
    path: "/internal/downloads/fail",
    access: "internal",
    summary: "다운로드 실패. 이유는 코드로",
    body: t.Object({
      downloadId: Uuid,
      errorCode: DownloadErrorCodeSchema,
      message: t.String({ maxLength: 500 }),
      removedToken: t.Optional(
        t.String({
          pattern: "^[0-9a-f]{10}$",
          description: "GALLERY_REMOVED only: token checked on both EH and authenticated ExH gallery pages",
        }),
      ),
      spentGp: t.Optional(t.Integer({ minimum: 0 })),
    }),
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "INVALID_SIGNATURE"],
    notes:
      "GALLERY_REMOVED는 running Archive의 form 단계·spentGp=0·현재 토큰과 일치하는 removedToken일 때만 수락한다. 양쪽 원본의 명시적 삭제를 확인한 Lambda만 보고하며, 갤러리 상태와 감사 기록을 같은 트랜잭션에서 반영한다.",
  }),
  "internal.downloadHandoff": route({
    module: "internal",
    method: "POST",
    path: "/internal/downloads/handoff",
    access: "internal",
    summary: "Archive Lambda가 구매 전에 큰 작업을 EC2 실행기로 넘긴다",
    body: t.Object({ downloadId: Uuid, estimatedBytes: t.Integer({ minimum: 1 }) }),
    responses: { 200: Ok },
    errors: ["NOT_FOUND", "CONFLICT", "INVALID_SIGNATURE"],
    notes:
      "running·form 단계의 Lambda Archive 작업만 받는다. 작업은 전역 슬롯을 유지한 채 queued·runner=ec2가 되고 Lambda 재호출 대상에서 빠진다. GP는 쓰지 않았다.",
  }),
  "internal.downloadClaim": route({
    module: "internal",
    method: "POST",
    path: "/internal/downloads/claim",
    access: "internal",
    summary: "EC2 실행기가 넘겨받은 Archive 작업 하나를 가져간다",
    body: t.Object({ maxBytes: t.Integer({ minimum: 0 }) }),
    responses: {
      200: t.Object({
        downloadId: nullable(Uuid),
        estimatedBytes: nullable(t.Integer({ minimum: 0, description: "archive estimate from the handoff" })),
      }),
    },
    errors: ["INVALID_SIGNATURE"],
    notes:
      "runner=ec2인 슬롯 확보 작업 중 추정 크기가 maxBytes 이하인 것을 우선순위·FIFO로 하나 돌려준다. 같은 작업은 60초 안에 다시 나가지 않으며 실제 소유는 downloadStart가 정한다. 호출은 대기 중인 EC2 작업의 생존 신호다.",
  }),
  "internal.tagPolicies": route({
    module: "internal",
    method: "GET",
    path: "/internal/tag-policies",
    access: "internal",
    summary: "현재 태그 정책. Lambda가 구매·다운로드 직전에 스스로 확인하는 용도",
    responses: {
      200: t.Object({
        fixed: t.Array(Tag, { minItems: 1, description: "central DB hard rules" }),
        hardValidForMs: t.Integer({ minimum: 1, maximum: 60000 }),
        rules: t.Array(TagPolicyRule),
        updatedAt: nullable(IsoDateTime),
      }),
    },
    errors: ["INVALID_SIGNATURE"],
  }),
});
